import get from 'lodash.get';
import { fetchUserMetadata } from '../helpers/fetchUserMetadata';
import { authTrackingHandler } from './authTrackingHandler';
import { trackRegistrationError } from './authTracking';
import { registrationMutation } from './registration/registrationMutation';
import { validateEmail, validateEmailString } from './authValidation';
import authCopy from './authCopy';
import { completeAccountEmailMutation } from './registration/completeAccountEmailMutation';
import authModalLoader from '../../utils/AuthModalLoader';

/**
 * helper function which handles email harvesting flows
 * accepts an email from an input (i.e. follow search banner, footer subscribe)
 * checks status of that email:
 *  a) if email is attached to existing user, prompt them to login or create PW for their account
 *  b) if email is not registered, create email only account for that user
 *
 * @param {String} email email being checked
 * @param {Function} errorHandler function for error handling
 * @param {Function} loadingHandler function for loading state handling
 * @param {Function} userRegistrationHandler callback after email only account is created for user, passed user data
 * @param {Function} userLoginHandler callback after user logs in, passed user data
 * @param {Object} environment relay environment
 * @param {String} gaLabel label value for auth
 * @param {String} flow auth flow constant
 * @param {Boolean} marketingOptIn has the user opted in to marketing campaign
 * @param {Boolean} isGdprApplicable is the user in EU or GDPR-covered region
 */
export function registrationOrLoginHandler({
    email,
    errorHandler = () => {},
    loadingHandler = () => {},
    userRegistrationHandler = () => {},
    userLoginHandler = () => {},
    showAuthHandler = authModalLoader.show.bind(authModalLoader),
    authType = authModalLoader.constants.AUTH_MODAL,
    environment,
    gaLabel,
    flow = authModalLoader.constants.DEFAULT_FLOW,
    marketingOptIn = true,
    isGdprApplicable = true,
}) {
    const validationError = validateEmail(email);
    if (validationError) {
        const errorString = validateEmailString(email);
        trackRegistrationError({ field: 'email', error: errorString });
        errorHandler(validationError);
        return;
    }
    loadingHandler(true);
    fetchUserMetadata(email, environment).then(
        ({ disableCompleteAccountEmail, userExistsMetadata }) => {
            if (!get(userExistsMetadata, 'userExists')) {
                registrationMutation(environment, {
                    email,
                    marketingOptIn: !isGdprApplicable || marketingOptIn, // automatically opt-in non-GDPR to marketing
                })
                    .then(({ userRegistration }) => {
                        const ga = { label: gaLabel };
                        authTrackingHandler({ userRegistration, environment, ga });
                        if (!disableCompleteAccountEmail) {
                            completeAccountEmailMutation({ email, environment });
                        }
                        userRegistrationHandler(userRegistration);
                    })
                    .catch(() => {
                        loadingHandler(false);
                        errorHandler(authCopy.errors.support);
                    });
            } else {
                trackRegistrationError({
                    field: 'email',
                    action: gaLabel,
                    error: 'existing email',
                });
                const authHandlerParams = {
                    showExistsError: true,
                    flow,
                    action: authModalLoader.constants.LOGIN,
                    ga: { label: gaLabel },
                    email,
                };
                switch (authType) {
                    case authModalLoader.constants.AUTH_MODAL:
                        showAuthHandler(authHandlerParams).then(userLoginHandler);
                        break;
                    case authModalLoader.constants.AUTH_CONTENT:
                        showAuthHandler({
                            ...authHandlerParams,
                            onAuthComplete: userLoginHandler,
                        });
                        break;
                }
            }
        }
    );
}
